/* Tent Selector — Trail Journey
 *
 * Hand-drawn trail metaphor: 7 numbered stops along a winding path.
 * The active stop pulses peach. The tent marker animates between stops.
 * Tiles are illustration-led with line-art SVG icons (no photography).
 *
 * Tune scoring by editing PRODUCTS / QUESTIONS / hard rules.
 */
(function () {
  'use strict';

  const SAGE = '#1d3330';
  const CREAM = '#FBF7F0';
  const PEACH = '#FFDDBF';

  /* -------- Questions (7 — added double_bed) -------- */
  const QUESTIONS = [
    {
      id: 'group',
      label: "Who's it for?",
      tag: 'The party',
      options: [
        { id: 'couple',     icon: 'couple',     image: 'couple',     label: 'Just me / a partner', sublabel: '1 to 2 people' },
        { id: 'small_fam',  icon: 'family',     image: 'small-fam',  label: 'Family of 3 or 4',    sublabel: 'Including kids' },
        { id: 'group',      icon: 'group',      image: 'group',      label: 'Group of 5+',         sublabel: 'Friends or extended family' },
        { id: 'commercial', icon: 'commercial', image: 'commercial', label: 'Commercial / hire',   sublabel: 'For my business' },
        { id: 'garden',     icon: 'garden',     image: 'garden',     label: 'Garden retreat',      sublabel: 'A space at home' }
      ]
    },
    {
      id: 'season',
      label: 'When will you use it?',
      tag: 'The season',
      options: [
        { id: 'summer',     icon: 'sun',  image: 'summer',     label: 'Summer only',      sublabel: 'June to August' },
        { id: 'shoulder',   icon: 'leaf', image: 'spring',     label: 'Spring to autumn', sublabel: 'Most of the year' },
        { id: 'year_round', icon: 'snow', image: 'year-round', label: 'Year-round',       sublabel: 'Including winter' },
        { id: 'festival',   icon: 'flag', image: 'festival',   label: 'Festivals only',   sublabel: 'A few weekends' }
      ]
    },
    {
      id: 'experience',
      label: 'How experienced are you?',
      tag: 'The know-how',
      options: [
        { id: 'first',     icon: 'tent',       image: 'first-tent',   label: 'First tent',     sublabel: 'New to this' },
        { id: 'some',      icon: 'compass',    image: 'camped-a-bit', label: 'Camped a bit',   sublabel: 'A few trips under my belt' },
        { id: 'seasoned',  icon: 'cabin',      image: 'seasoned',     label: 'Seasoned',       sublabel: 'I know my way around canvas' },
        { id: 'trade',     icon: 'commercial', image: 'trade-buyer',  label: 'Trade buyer',    sublabel: 'Buying for a business' }
      ]
    },
    {
      id: 'stove',
      label: 'Will you use a wood-burning stove?',
      tag: 'The stove',
      options: [
        { id: 'no',     icon: 'no_circle', image: 'no-stove',    label: 'No',              sublabel: "Won't be needing one" },
        { id: 'maybe',  icon: 'question',  image: 'maybe-stove', label: 'Maybe later',     sublabel: 'Want the option open' },
        { id: 'yes',    icon: 'stove',     image: 'stove',       label: 'Yes, definitely', sublabel: 'Stove use is a must' }
      ]
    },
    {
      id: 'priority',
      label: 'What matters most to you?',
      tag: 'The priority',
      options: [
        { id: 'light',   icon: 'feather',     image: 'feather',        label: 'Lightest to pitch', sublabel: 'Easy solo setup' },
        { id: 'cold',    icon: 'thermometer', image: 'best-in-cold',   label: 'Best in cold',      sublabel: 'Warm, all-weather' },
        { id: 'feel',    icon: 'diamond',     image: 'premium-feel',   label: 'Best feel inside',  sublabel: 'Premium materials' },
        { id: 'price',   icon: 'coin',        image: 'best-price',     label: 'Best price',        sublabel: 'Keep it affordable' }
      ]
    },
    {
      id: 'budget',
      label: 'Roughly, what is your budget?',
      tag: 'The budget',
      options: [
        { id: 'u300',  icon: 'coin',    image: 'coin',          label: 'Under £300',   sublabel: 'Entry level' },
        { id: 'u500',  icon: 'coin',    image: 'few-coins',     label: '£300 to £500', sublabel: 'Mid-range' },
        { id: 'u800',  icon: 'diamond', image: 'more-coins',    label: '£500 to £800', sublabel: 'Premium' },
        { id: 'high',  icon: 'diamond', image: 'pile-of-coins', label: '£800+',        sublabel: 'Top of the range' },
        { id: 'open',  icon: 'compass', image: 'open',          label: 'Not fussed',   sublabel: 'Show me the right one' }
      ]
    },
    {
      id: 'double_bed',
      label: 'Need room for a double bed?',
      tag: 'The bed',
      options: [
        { id: 'yes', icon: 'bed',       image: 'double-bed',   label: 'Yes',     sublabel: 'Standard double, 135cm wide' },
        { id: 'no',  icon: 'no_circle', image: 'camping-roll', label: 'No need', sublabel: 'Sleeping mats or bunks' }
      ]
    },
    {
      id: 'gear',
      label: 'How much gear are you bringing?',
      tag: 'The kit',
      options: [
        { id: 'light',    icon: 'feather', image: 'essentials', label: 'Light pack',   sublabel: 'Just the essentials' },
        { id: 'standard', icon: 'bag',     image: 'some-gear',  label: 'Standard',     sublabel: 'Typical camping kit' },
        { id: 'lots',     icon: 'crate',   image: 'lots-gear',  label: 'Lots of gear', sublabel: 'We want it all inside' }
      ]
    }
  ];

  /* -------- Line-art icon set (24×24 viewBox via 64×64 paths) -------- */
  const ICONS = {
    tent:    '<path d="M32 8 L10 54 L54 54 Z" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M32 8 L32 54" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M26 54 L26 44 Q32 40 38 44 L38 54" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M32 4 L32 12" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/><circle cx="32" cy="3" r="1.6" fill="#1d3330"/>',
    couple:  '<circle cx="24" cy="22" r="6" fill="none" stroke="#1d3330" stroke-width="1.6"/><circle cx="42" cy="22" r="6" fill="none" stroke="#1d3330" stroke-width="1.6"/><path d="M16 50 Q16 36 24 36 Q32 36 32 50" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M32 50 Q32 36 42 36 Q50 36 50 50" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M30 28 Q33 32 36 28" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/>',
    family:  '<circle cx="18" cy="20" r="5" fill="none" stroke="#1d3330" stroke-width="1.6"/><circle cx="34" cy="20" r="5" fill="none" stroke="#1d3330" stroke-width="1.6"/><circle cx="48" cy="28" r="3.5" fill="none" stroke="#1d3330" stroke-width="1.6"/><path d="M11 50 Q11 32 18 32 Q25 32 25 50" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M27 50 Q27 32 34 32 Q41 32 41 50" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M44 50 Q44 38 48 38 Q52 38 52 50" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>',
    group:   '<circle cx="12" cy="22" r="5" fill="none" stroke="#1d3330" stroke-width="1.6"/><circle cx="28" cy="22" r="5" fill="none" stroke="#1d3330" stroke-width="1.6"/><circle cx="44" cy="22" r="5" fill="none" stroke="#1d3330" stroke-width="1.6"/><circle cx="56" cy="26" r="3.5" fill="none" stroke="#1d3330" stroke-width="1.6"/><path d="M5 52 Q5 34 12 34 Q19 34 19 52" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M21 52 Q21 34 28 34 Q35 34 35 52" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M37 52 Q37 34 44 34 Q51 34 51 52" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>',
    commercial: '<path d="M14 40 L4 56 L24 56 Z" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M32 32 L20 56 L44 56 Z" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M50 38 L40 56 L60 56 Z" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M2 58 L62 58" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-dasharray="2 4" opacity="0.5" stroke-linecap="round"/>',
    garden:  '<path d="M20 32 L8 54 L32 54 Z" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M48 44 Q48 36 54 36 Q60 36 60 44 L60 54 L48 54 Z" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M44 28 Q44 22 50 22 Q56 22 54 30" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/><path d="M2 56 L62 56" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/>',
    sun:     '<circle cx="32" cy="32" r="10" fill="none" stroke="#1d3330" stroke-width="1.6"/><line x1="48" y1="32" x2="54" y2="32" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/><line x1="10" y1="32" x2="16" y2="32" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/><line x1="32" y1="48" x2="32" y2="54" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/><line x1="32" y1="10" x2="32" y2="16" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/><line x1="43.3" y1="43.3" x2="47.6" y2="47.6" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/><line x1="16.4" y1="16.4" x2="20.7" y2="20.7" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/><line x1="20.7" y1="43.3" x2="16.4" y2="47.6" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/><line x1="47.6" y1="16.4" x2="43.3" y2="20.7" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/>',
    leaf:    '<path d="M12 50 Q12 18 44 14 Q52 38 24 52 Z" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M14 48 Q26 36 42 18" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/>',
    snow:    '<path d="M32 8 L32 56" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/><path d="M10 32 L54 32" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/><path d="M16 16 L48 48" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/><path d="M48 16 L16 48" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/>',
    flag:    '<path d="M16 56 L16 8" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/><path d="M16 10 Q28 14 40 10 Q44 22 32 26 Q20 22 16 26 Z" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M2 58 L62 58" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-dasharray="2 4" opacity="0.5" stroke-linecap="round"/>',
    stove:   '<path d="M18 24 L46 24 L46 52 L18 52 Z" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linejoin="round"/><circle cx="32" cy="38" r="6" fill="none" stroke="#1d3330" stroke-width="1.6"/><path d="M28 38 Q31 32 34 38" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/><path d="M32 24 L32 8" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/><path d="M30 12 Q32 8 34 12" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/><path d="M30 4 Q32 0 34 4" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/>',
    no_circle: '<circle cx="32" cy="32" r="22" fill="none" stroke="#1d3330" stroke-width="1.6"/><path d="M18 18 L46 46" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/>',
    question: '<circle cx="32" cy="32" r="22" fill="none" stroke="#1d3330" stroke-width="1.6"/><path d="M24 24 Q24 16 32 16 Q40 16 40 24 Q40 30 32 32 L32 38" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><circle cx="32" cy="46" r="1.8" fill="#1d3330"/>',
    feather: '<path d="M14 54 L46 12" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/><path d="M22 46 Q34 28 44 14" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/><path d="M28 40 Q38 28 44 16" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/><path d="M34 34 Q42 24 44 18" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/>',
    thermometer: '<path d="M26 8 Q26 4 32 4 Q38 4 38 8 L38 38 Q44 42 44 48 Q44 56 32 56 Q20 56 20 48 Q20 42 26 38 Z" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linejoin="round"/><circle cx="32" cy="48" r="5" fill="#1d3330"/><path d="M32 14 L32 38" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/>',
    diamond: '<path d="M14 24 L32 8 L50 24 L32 56 Z" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linejoin="round"/><path d="M14 24 L50 24" stroke="#1d3330" stroke-width="1.6"/><path d="M22 24 L32 8 L42 24" fill="none" stroke="#1d3330" stroke-width="1.6"/><path d="M22 24 L32 56" stroke="#1d3330" stroke-width="1.6"/><path d="M42 24 L32 56" stroke="#1d3330" stroke-width="1.6"/>',
    coin:    '<circle cx="32" cy="32" r="20" fill="none" stroke="#1d3330" stroke-width="1.6"/><circle cx="32" cy="32" r="14" fill="none" stroke="#1d3330" stroke-width="1.6"/><path d="M28 24 L36 40" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/><path d="M36 24 L28 40" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/>',
    compass: '<circle cx="32" cy="32" r="24" fill="none" stroke="#1d3330" stroke-width="1.6"/><circle cx="32" cy="32" r="3" fill="#1d3330"/><path d="M32 12 L36 32 L32 52 L28 32 Z" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linejoin="round"/>',
    cabin:   '<path d="M10 56 L10 28 L32 12 L54 28 L54 56 Z" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linejoin="round"/><path d="M26 56 L26 38 L38 38 L38 56" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linejoin="round"/><path d="M2 58 L62 58" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-dasharray="2 4" opacity="0.5" stroke-linecap="round"/>',
    bed:     '<path d="M6 38 L6 50 L58 50 L58 38" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M6 50 L6 56" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/><path d="M58 50 L58 56" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/><path d="M10 38 L10 32 Q10 28 14 28 L50 28 Q54 28 54 32 L54 38" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linejoin="round"/><path d="M14 28 L14 22 Q14 20 16 20 L48 20 Q50 20 50 22 L50 28" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linejoin="round"/>',
    bag:     '<path d="M18 24 L18 18 Q18 10 32 10 Q46 10 46 18 L46 24" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 24 L52 24 L50 56 L14 56 Z" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linejoin="round"/><path d="M22 32 L42 32" stroke="#1d3330" stroke-width="1.6" stroke-linecap="round"/>',
    crate:   '<path d="M8 26 L32 14 L56 26 L56 50 L32 58 L8 50 Z" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linejoin="round"/><path d="M8 26 L32 38 L56 26" fill="none" stroke="#1d3330" stroke-width="1.6" stroke-linejoin="round"/><path d="M32 38 L32 58" stroke="#1d3330" stroke-width="1.6"/><path d="M20 21 L44 33" stroke="#1d3330" stroke-width="1.6" stroke-dasharray="2 3" opacity="0.5"/>'
  };

  function iconSvg(name, size) {
    const inner = ICONS[name] || ICONS.question;
    return `<svg viewBox="0 0 64 64" width="${size}" height="${size}" aria-hidden="true">${inner}</svg>`;
  }

  /* -------- Products (13 stocked SKUs) -------- */
  const PRODUCTS = [
    { sku: 'BBT1',  size: 3, fabric: 'oxford',     price: 229, popularity: 10,
      variant_match: { size: '3m', fabric: 'oxford' },
      traits: { capacity: 3, summer: 9, shoulder: 6, winter: 2, stove: 0, weight: 10, premium: 4, value: 10 },
      pitch: 'Light enough to pitch solo, perfect for couples or first-timers in summer.' },
    { sku: 'BBT3',  size: 3, fabric: 'polycotton', price: 319, popularity: 4,
      variant_match: { size: '3m', fabric: 'polycotton' },
      traits: { capacity: 3, summer: 8, shoulder: 8, winter: 5, stove: 8, weight: 7, premium: 6, value: 7 },
      pitch: 'A breathable, all-rounder 3m built for couples who want stove-readiness.' },
    { sku: 'BBT2',  size: 4, fabric: 'oxford',     price: 339, popularity: 9,
      variant_match: { size: '4m', fabric: 'oxford' },
      traits: { capacity: 6, summer: 9, shoulder: 6, winter: 2, stove: 0, weight: 9, premium: 4, value: 9 },
      pitch: 'The lightweight 4m, ideal for a small family in summer with easy pitching.' },
    { sku: 'BBT5',  size: 4, fabric: 'polycotton', price: 419, popularity: 5,
      variant_match: { size: '4m', fabric: 'polycotton' },
      traits: { capacity: 6, summer: 8, shoulder: 8, winter: 5, stove: 8, weight: 6, premium: 6, value: 7 },
      pitch: 'A balanced 4m for families who camp through spring and autumn.' },
    { sku: 'BBT6',  size: 4, fabric: 'pure_cotton', price: 429, popularity: 5,
      variant_match: { size: '4m', fabric: 'pure cotton' },
      traits: { capacity: 6, summer: 7, shoulder: 9, winter: 7, stove: 8, weight: 5, premium: 8, value: 7 },
      pitch: 'Premium pure cotton 4m, naturally insulating for cooler nights.' },
    { sku: 'BBT8',  size: 4, fabric: 'fire_cotton', price: 539, popularity: 3,
      variant_match: { size: '4m', fabric: 'fire cotton' },
      traits: { capacity: 6, summer: 7, shoulder: 9, winter: 10, stove: 10, weight: 4, premium: 9, value: 6 },
      pitch: 'Year-round 4m with a built-in stove flap, made for serious campers.' },
    { sku: 'BBT4',  size: 5, fabric: 'oxford',     price: 379, popularity: 8,
      variant_match: { size: '5m', fabric: 'oxford' },
      traits: { capacity: 9, summer: 9, shoulder: 6, winter: 2, stove: 0, weight: 8, premium: 4, value: 9 },
      pitch: 'A roomy 5m at the lightest fabric weight, big enough for groups in summer.' },
    { sku: 'BBT7',  size: 5, fabric: 'polycotton', price: 519, popularity: 6,
      variant_match: { size: '5m', fabric: 'polycotton' },
      traits: { capacity: 9, summer: 8, shoulder: 8, winter: 5, stove: 8, weight: 5, premium: 6, value: 7 },
      pitch: 'A 5m polycotton group tent, breathable and stove-ready.' },
    { sku: 'BBT9',  size: 5, fabric: 'pure_cotton', price: 519, popularity: 7,
      variant_match: { size: '5m', fabric: 'pure cotton' },
      traits: { capacity: 9, summer: 7, shoulder: 9, winter: 7, stove: 8, weight: 4, premium: 8, value: 7 },
      pitch: 'Pure cotton 5m, the premium choice for a temperature-regulating group setup.' },
    { sku: 'BBT10', size: 5, fabric: 'fire_cotton', price: 665, popularity: 3,
      variant_match: { size: '5m', fabric: 'fire cotton' },
      traits: { capacity: 9, summer: 7, shoulder: 9, winter: 10, stove: 10, weight: 3, premium: 9, value: 6 },
      pitch: 'Our year-round 5m, with stove flap factory-installed for genuine winter use.' },
    { sku: 'BBT11', size: 6, fabric: 'pure_cotton', price: 695, popularity: 1,
      variant_match: { size: '6m', fabric: 'pure cotton' },
      traits: { capacity: 10, summer: 7, shoulder: 9, winter: 7, stove: 8, weight: 3, premium: 9, value: 6 },
      pitch: '6m pure cotton, made for big gatherings and commercial use.' },
    { sku: 'BC12',  size: 6, fabric: 'fire_cotton', price: 849, popularity: 1,
      variant_match: { size: '6m', fabric: 'fire cotton' },
      traits: { capacity: 10, summer: 7, shoulder: 9, winter: 10, stove: 10, weight: 2, premium: 10, value: 5 },
      pitch: '6m all-season Fire Cotton, the hire-business workhorse with built-in stove flap.' },
    { sku: 'BT13',  size: 7, fabric: 'fire_cotton', price: 1095, popularity: 1,
      variant_match: { size: '7m', fabric: 'fire cotton' },
      traits: { capacity: 10, summer: 7, shoulder: 9, winter: 10, stove: 10, weight: 1, premium: 10, value: 4 },
      pitch: 'The flagship 7m Fire Cotton — top of the range, year-round, built for commercial use.' }
  ];

  const FABRIC_LABEL = {
    oxford:      'Oxford Ultralight',
    polycotton:  'Polycotton',
    pure_cotton: 'Pure Cotton',
    fire_cotton: 'Fire Cotton'
  };

  /* -------- Scoring -------- */
  function scoreProduct(p, a) {
    let s = 0;
    const sizeMap = { couple: [3, 3], small_fam: [6, 3], group: [9, 3], commercial: [10, 2.5], garden: [6, 2] };
    if (sizeMap[a.group]) {
      const [ideal, weight] = sizeMap[a.group];
      s += weight * (10 - Math.abs(p.traits.capacity - ideal));
    }

    if (a.season === 'summer')      s += p.traits.summer * 1.4;
    if (a.season === 'shoulder')    s += p.traits.shoulder * 1.4;
    if (a.season === 'year_round')  s += p.traits.winter * 2.2;
    if (a.season === 'festival')    s += p.traits.summer * 1.2 + p.traits.weight * 0.4;

    if (a.experience === 'first')    s += p.traits.weight * 0.6 + (p.fabric === 'oxford' ? 6 : 0);
    if (a.experience === 'some')     s += p.traits.shoulder * 0.4;
    if (a.experience === 'seasoned') s += p.traits.premium * 0.5;
    if (a.experience === 'trade')    s += p.traits.premium * 0.4 + (p.size >= 5 ? 4 : 0);

    if (a.stove === 'no')    s += (p.fabric === 'oxford' ? 4 : 0);
    if (a.stove === 'maybe') s += p.traits.stove * 0.4;
    if (a.stove === 'yes')   s += p.traits.stove * 1.8;

    if (a.priority === 'light') s += p.traits.weight * 1.5;
    if (a.priority === 'cold')  s += p.traits.winter * 1.5;
    if (a.priority === 'feel')  s += p.traits.premium * 1.5;
    if (a.priority === 'price') s += p.traits.value * 1.5;

    const cap = budgetLimit(a.budget);
    if (cap && p.price > cap) s -= 6;
    if (a.budget === 'open' && p.traits.premium >= 8) s += 2;

    s += p.popularity * 0.5;
    return s;
  }

  function budgetLimit(b) {
    return { u300: 300, u500: 500, u800: 800 }[b] || null;
  }

  function passesHardRules(p, a) {
    if (a.stove === 'yes' && p.fabric === 'oxford') return false;
    if (a.season === 'year_round' && p.fabric === 'oxford') return false;
    if (a.group === 'group' && p.size < 5) return false;
    if (a.group === 'commercial' && p.size < 5) return false;
    if (a.double_bed === 'yes' && p.size < 4) return false;
    // Anything other than a light pack rules out the 3m — even a "standard" load fills it.
    if (a.gear && a.gear !== 'light' && p.size < 4) return false;
    const cap = budgetLimit(a.budget);
    if (cap && p.price > cap * 1.4) return false;
    return true;
  }

  function rank(answers) {
    const filtered = PRODUCTS.filter(p => passesHardRules(p, answers));
    const pool = filtered.length ? filtered : PRODUCTS.slice();
    return pool
      .map(p => ({ ...p, score: scoreProduct(p, answers) }))
      .sort((a, b) => b.score - a.score);
  }

  /* -------- Why-this-one reasons -------- */
  function reasonsFor(p, a) {
    const r = [];
    if (a.group === 'couple' && p.size === 3) r.push('You said 1 to 2 people — a 3m fits cosily.');
    if (a.group === 'small_fam' && p.size === 4) r.push('Family of 3 to 4 — a 4m gives proper room without being a beast to pitch.');
    if (a.group === 'group' && p.size >= 5) r.push('Group of 5+ — this size sleeps a crowd with floor space to spare.');
    if (a.group === 'commercial' && p.size >= 5) r.push('Commercial use — the larger sizes earn their keep on hire.');
    if (a.group === 'garden') r.push('As a garden retreat, this size strikes a nice balance.');

    if (a.season === 'year_round' && p.traits.winter >= 9) r.push('Year-round use — Fire Cotton is built for winter with a stove flap factory-installed.');
    if (a.season === 'summer' && p.fabric === 'oxford') r.push('Summer-only use — Oxford keeps things light, dries fast, and packs small.');
    if (a.season === 'festival' && p.fabric === 'oxford') r.push('Festival use — Oxford is the easiest to throw in the car and pitch on arrival.');
    if (a.season === 'shoulder' && (p.fabric === 'polycotton' || p.fabric === 'pure_cotton')) r.push('Spring to autumn — breathable cotton blends handle changeable weather best.');

    if (a.stove === 'yes' && p.traits.stove >= 9) r.push('Stove use confirmed — Fire Cotton ships with a stove flap pre-installed, no DIY required.');
    if (a.stove === 'maybe' && p.traits.stove >= 8) r.push('Keeping the stove option open — this fabric supports it (DIY flap install required).');

    if (a.priority === 'light' && p.traits.weight >= 8) r.push('You wanted easy pitching — this is the lightest in the range.');
    if (a.priority === 'cold' && p.traits.winter >= 9) r.push('Best in cold — natural cotton fibres hold heat far better than synthetics.');
    if (a.priority === 'feel' && p.traits.premium >= 8) r.push('Best feel inside — natural fabric softens light and breathes better than synthetics.');
    if (a.priority === 'price' && p.traits.value >= 9) r.push('Best price — this is the entry into the Boho range without compromise on weather-proofing.');

    if (a.experience === 'first' && p.fabric === 'oxford') r.push('First tent — Oxford is forgiving to pitch and dries quickly if you pack it damp.');
    if (a.double_bed === 'yes' && p.size >= 4) r.push('A double bed fits comfortably with floor to spare for kit.');
    if (a.gear === 'lots' && p.size >= 4) r.push('Plenty of gear — this size keeps your kit inside the tent, not under a tarp outside.');
    else if (a.gear === 'standard' && p.size >= 4) r.push('Standard kit — a 4m or larger gives you room for the gear and the people.');

    return r.slice(0, 4);
  }

  /* -------- Variant URL lookup -------- */
  function findVariant(p, variants) {
    if (!variants || !variants.length) return null;
    const sizeStr = p.variant_match.size.toLowerCase();
    const fabricStr = p.variant_match.fabric.toLowerCase();
    return variants.find(v => {
      if (!v.options) return false;
      const opts = v.options.map(o => (o || '').toLowerCase());
      return opts.some(o => o.includes(sizeStr)) && opts.some(o => o.includes(fabricStr));
    }) || null;
  }

  /* -------- Trail SVG ------- */
  const TRAIL_W = 1100;
  const TRAIL_H = 220;

  function stopPoint(i, total) {
    const t = i / (total - 1);
    const x = 60 + t * (TRAIL_W - 120);
    const y = 110 + Math.sin(t * Math.PI * 2.4) * 56;
    return { x, y };
  }

  function trailPath(total) {
    let d = '';
    for (let i = 0; i < total; i++) {
      const p = stopPoint(i, total);
      if (i === 0) {
        d += `M${p.x} ${p.y} `;
      } else {
        const prev = stopPoint(i - 1, total);
        const cx = (prev.x + p.x) / 2;
        d += `Q${cx} ${prev.y} ${p.x} ${p.y} `;
      }
    }
    return d.trim();
  }

  function renderTrail(wrap, step, total, markerUrl) {
    const idx = Math.max(0, Math.min(step, total - 1));
    const d = trailPath(total);
    const dashLen = 2400;
    const completedOffset = total > 1 ? dashLen - (idx / (total - 1)) * dashLen : dashLen;
    const marker = stopPoint(idx, total);

    let stopsHtml = '';
    for (let i = 0; i < total; i++) {
      const sp = stopPoint(i, total);
      const done = i < step;
      const active = i === step;
      const fill = active ? PEACH : (done ? SAGE : CREAM);
      const innerNumOrCheck = done
        ? `<path d="M-7 0 L-2 5 L7 -4" fill="none" stroke="${CREAM}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>`
        : `<text text-anchor="middle" y="5" font-size="13" font-family="Fraunces, Georgia, serif" fill="${SAGE}" font-weight="500">${i + 1}</text>`;
      const pulse = active
        ? `<circle r="26" fill="none" stroke="${PEACH}" stroke-width="2" class="ts-stop-pulse" style="opacity:0.6"/>`
        : '';
      const tag = QUESTIONS[i] && QUESTIONS[i].tag ? QUESTIONS[i].tag.toUpperCase() : '';
      stopsHtml += `
        <g transform="translate(${sp.x} ${sp.y})">
          ${pulse}
          <circle r="18" fill="${fill}" stroke="${SAGE}" stroke-width="1.6"/>
          ${innerNumOrCheck}
          <text text-anchor="middle" y="42" font-size="10.5" font-family="DM Mono, monospace" fill="${SAGE}" letter-spacing="0.1em" style="opacity:${active ? 1 : 0.55}">${tag}</text>
        </g>
      `;
    }

    wrap.innerHTML = `
      <svg viewBox="0 0 ${TRAIL_W} ${TRAIL_H}" preserveAspectRatio="none">
        <path d="${d}" fill="none" stroke="${SAGE}" stroke-opacity="0.2" stroke-width="2" stroke-dasharray="2 6" stroke-linecap="round"/>
        <path d="${d}" fill="none" stroke="${SAGE}" stroke-width="2" stroke-linecap="round"
          class="ts-trail-completed"
          style="stroke-dasharray: ${dashLen}; stroke-dashoffset: ${completedOffset};"/>
        ${stopsHtml}
        <g class="ts-trail-marker" style="transform: translate(${marker.x}px, ${marker.y - 42}px);">
          ${markerUrl
            ? `<image href="${markerUrl}" x="-32" y="-17" width="64" height="33" preserveAspectRatio="xMidYMid meet"/>`
            : `<g transform="translate(-12 -12)"><svg width="24" height="24" viewBox="0 0 64 64" x="0" y="0">${ICONS.tent}</svg></g>`}
        </g>
      </svg>
    `;
  }

  /* -------- GA4 -------- */
  function track(event, payload) {
    if (typeof window.dataLayer !== 'undefined') {
      window.dataLayer.push({ event, ...payload });
    }
    if (typeof window.gtag === 'function') {
      window.gtag('event', event, payload);
    }
  }

  /* -------- Quiz state machine -------- */
  function init(root) {
    const configEl = root.querySelector('[data-ts-config]');
    let config = { variants: [], product_url: '/products/classic-bell-tent', trust: {} };
    try { config = JSON.parse(configEl.textContent); } catch (e) {}

    const screens = {
      intro:    root.querySelector('[data-ts-screen="intro"]'),
      question: root.querySelector('[data-ts-screen="question"]'),
      email:    root.querySelector('[data-ts-screen="email"]'),
      loading:  root.querySelector('[data-ts-screen="loading"]'),
      result:   root.querySelector('[data-ts-screen="result"]'),
      trade:    root.querySelector('[data-ts-screen="trade"]')
    };
    const trailWrap   = root.querySelector('[data-ts-trail-wrap]');
    const progressM   = root.querySelector('[data-ts-progress-mobile] > span');
    const counterEl   = root.querySelector('[data-ts-counter]');
    const questionEl  = root.querySelector('[data-ts-question-text]');
    const optionsEl   = root.querySelector('[data-ts-options]');
    const startBtn    = root.querySelector('[data-ts-start]');
    const skipBtn     = root.querySelector('[data-ts-skip]');
    const emailForm   = root.querySelector('[data-ts-email-form]');
    const emailInput  = root.querySelector('[data-ts-email-input]');
    const emailErr    = root.querySelector('[data-ts-email-error]');
    const emailNews   = root.querySelector('[data-ts-email-newsletter]');
    const emailSkip   = root.querySelector('[data-ts-email-skip]');

    const state = { step: -1, answers: {} };
    const total = QUESTIONS.length;

    function show(screenName) {
      Object.entries(screens).forEach(([k, el]) => {
        if (!el) return;
        if (k === screenName) {
          el.hidden = false;
          requestAnimationFrame(() => el.classList.add('is-active'));
        } else {
          el.classList.remove('is-active');
          el.hidden = true;
        }
      });
    }

    function updateCounter() {
      const inQuestion = state.step >= 0 && state.step < total;
      if (state.step < 0) {
        counterEl.textContent = '';
        trailWrap.style.display = 'none';
      } else if (state.step >= total) {
        counterEl.textContent = 'You are here';
        trailWrap.style.display = 'none';
      } else {
        counterEl.textContent = `Stop ${state.step + 1} of ${total}`;
        trailWrap.style.display = '';
        renderTrail(trailWrap, state.step, total, config.marker_image);
      }
      if (progressM) {
        progressM.style.width = inQuestion
          ? `${((state.step + 1) / total) * 100}%`
          : (state.step >= total ? '100%' : '0%');
      }
    }

    function renderQuestion() {
      const q = QUESTIONS[state.step];
      questionEl.textContent = q.label;
      optionsEl.innerHTML = '';
      optionsEl.style.setProperty('--ts-cols', q.options.length);
      q.options.forEach(opt => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'tent-selector__option';
        btn.dataset.optionId = opt.id;
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-checked', state.answers[q.id] === opt.id ? 'true' : 'false');
        if (state.answers[q.id] === opt.id) btn.classList.add('is-selected');
        const imageUrl = opt.image && config.icons && config.icons[opt.image];
        const iconMarkup = imageUrl
          ? `<img src="${escapeAttr(imageUrl)}" alt="" loading="lazy" decoding="async">`
          : iconSvg(opt.icon, 44);
        btn.innerHTML = `
          <span class="tent-selector__option-icon">${iconMarkup}</span>
          <span class="tent-selector__option-label">${escapeHtml(opt.label)}</span>
          ${opt.sublabel ? `<span class="tent-selector__option-sublabel">${escapeHtml(opt.sublabel)}</span>` : ''}
        `;
        btn.addEventListener('click', () => answer(q.id, opt.id, btn));
        optionsEl.appendChild(btn);
      });
      updateCounter();
      track('tent_selector_step_view', { step: state.step + 1, question_id: q.id });
    }

    function answer(qId, optId, btn) {
      // Visually mark selected immediately
      optionsEl.querySelectorAll('.tent-selector__option').forEach(el => el.classList.remove('is-selected'));
      if (btn) btn.classList.add('is-selected');

      state.answers[qId] = optId;
      track('tent_selector_answer', { question_id: qId, answer_id: optId, step: state.step + 1 });

      // 280ms delay so user sees the selection
      setTimeout(() => {
        if (state.step + 1 < total) {
          state.step += 1;
          renderQuestion();
          show('question');
        } else {
          afterLastAnswer();
        }
      }, 280);
    }

    function afterLastAnswer() {
      // Trade buyers skip the email screen — they're going to the trade enquiry path
      const isTrade = state.answers.group === 'commercial' || state.answers.experience === 'trade';
      if (isTrade) {
        finish();
      } else {
        showEmailScreen();
      }
    }

    function showEmailScreen() {
      counterEl.textContent = 'Almost there';
      trailWrap.style.display = 'none';
      if (progressM) progressM.style.width = '100%';
      if (emailInput) {
        emailInput.value = '';
        emailInput.removeAttribute('aria-invalid');
      }
      if (emailErr) emailErr.hidden = true;
      show('email');
      track('tent_selector_email_view');
    }

    function skip() {
      const q = QUESTIONS[state.step];
      delete state.answers[q.id];
      track('tent_selector_skip', { question_id: q.id, step: state.step + 1 });
      if (state.step + 1 < total) {
        state.step += 1;
        renderQuestion();
      } else {
        afterLastAnswer();
      }
    }

    function finish() {
      show('loading');
      const ranked = rank(state.answers);
      const top = ranked[0];
      const isTrade = state.answers.group === 'commercial' || state.answers.experience === 'trade';

      setTimeout(() => {
        if (isTrade) {
          renderTrade(top);
          show('trade');
        } else {
          renderResult(top);
          show('result');
        }
        track('tent_selector_complete', {
          recommended_sku: top.sku,
          recommended_size: top.size + 'm',
          recommended_fabric: top.fabric,
          answers: state.answers
        });
      }, 900);
    }

    function renderResult(p) {
      const fabricLabel = FABRIC_LABEL[p.fabric] || p.fabric;
      const productName = `${p.size}m ${fabricLabel} Classic Bell Tent`;
      const variant = findVariant(p, config.variants);
      // Use the catalogue price (correct GBP) — variant.price comes through with shop's
      // money locale which can render with comma decimals or wrong currency.
      const priceDisplay = `£${p.price}`;
      const url = (variant && variant.url) ? variant.url : config.product_url;
      const reasons = reasonsFor(p, state.answers);
      const reassureForFirst = state.answers.experience === 'first';

      const reasonsHtml = reasons.map(r => `
        <li class="tent-selector__why-item">
          <svg class="tent-selector__why-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          <span>${escapeHtml(r)}</span>
        </li>
      `).join('');

      const reassure = reassureForFirst ? `
        <div class="tent-selector__reassure">
          <strong>First tent? Here is what to expect on day one.</strong>
          Most customers pitch their bell tent in 15 to 20 minutes the first time. The 3m and 4m sizes are easy enough to do solo. We have a step-by-step guide and video walking you through it before your first night out.
        </div>
      ` : '';

      const trust = `
        <div class="tent-selector__trust">
          <span class="tent-selector__trust-item">★ ${escapeHtml(config.trust.rating || '4.83')} from ${escapeHtml(config.trust.review_count || '227')} reviews</span>
          <span class="tent-selector__trust-item">Free UK shipping over £${escapeHtml(config.trust.shipping_threshold || '180')}</span>
          <span class="tent-selector__trust-item">Same-day dispatch before noon</span>
        </div>
      `;

      const resultEl = root.querySelector('[data-ts-result]');
      resultEl.innerHTML = `
        <div class="tent-selector__hero">
          <p class="tent-selector__hero-eyebrow">The destination</p>
          <h2 class="tent-selector__hero-title">${escapeHtml(productName)}</h2>
          <p class="tent-selector__hero-price">${priceDisplay}</p>
          <p class="tent-selector__hero-pitch">${escapeHtml(p.pitch)}</p>
          <a href="${escapeAttr(url)}" class="tent-selector__hero-cta" data-ts-shop-cta data-ts-sku="${p.sku}">Shop this tent</a>
        </div>
        <div class="tent-selector__why">
          <p class="tent-selector__why-title">Why this one</p>
          <ul class="tent-selector__why-list">${reasonsHtml || '<li class="tent-selector__why-item">A solid all-round match for what you told us.</li>'}</ul>
        </div>
        ${reassure}
        ${trust}
      `;

      // Append "Retrace your steps" link (replacing any prior one)
      const oldRetake = screens.result.querySelector('.tent-selector__retake');
      if (oldRetake) oldRetake.remove();
      const retake = document.createElement('button');
      retake.type = 'button';
      retake.className = 'tent-selector__retake';
      retake.textContent = 'Retrace your steps';
      retake.addEventListener('click', reset);
      screens.result.appendChild(retake);

      const shopCta = resultEl.querySelector('[data-ts-shop-cta]');
      if (shopCta) {
        shopCta.addEventListener('click', () => {
          track('tent_selector_shop_click', { sku: p.sku, size: p.size + 'm', fabric: p.fabric });
        });
      }
    }

    function renderTrade(p) {
      const recEl = root.querySelector('[data-ts-trade-recommend]');
      const showResultBtn = root.querySelector('[data-ts-trade-show]');
      if (recEl) recEl.textContent = `${p.size}m ${FABRIC_LABEL[p.fabric] || p.fabric}`;
      if (showResultBtn) {
        showResultBtn.addEventListener('click', () => {
          renderResult(p);
          show('result');
        }, { once: true });
      }
    }

    function reset() {
      state.step = 0;
      state.answers = {};
      renderQuestion();
      show('question');
      track('tent_selector_retake');
    }

    function isValidEmail(s) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((s || '').trim());
    }

    function submitToKlaviyo(email, wantsNewsletter, eventProps) {
      const k = config.klaviyo || {};
      if (!k.company_id) return; // no-op if not configured
      const headers = {
        'Content-Type': 'application/json',
        'revision': '2024-10-15'
      };
      // Subscribe to the newsletter list (if user opted in and a list is configured)
      if (wantsNewsletter && k.list_id) {
        try {
          fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(k.company_id), {
            method: 'POST',
            headers,
            body: JSON.stringify({
              data: {
                type: 'subscription',
                attributes: {
                  custom_source: 'Tent Selector Quiz',
                  profile: {
                    data: {
                      type: 'profile',
                      attributes: {
                        email,
                        subscriptions: {
                          email: { marketing: { consent: 'SUBSCRIBED' } }
                        }
                      }
                    }
                  }
                },
                relationships: {
                  list: { data: { type: 'list', id: k.list_id } }
                }
              }
            })
          }).catch(() => {});
        } catch (e) {}
      }
      // Fire a "Tent Selector Completed" event with the answer payload
      try {
        fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(k.company_id), {
          method: 'POST',
          headers,
          body: JSON.stringify({
            data: {
              type: 'event',
              attributes: {
                properties: eventProps,
                metric: { data: { type: 'metric', attributes: { name: 'Tent Selector Completed' } } },
                profile: { data: { type: 'profile', attributes: { email } } }
              }
            }
          })
        }).catch(() => {});
      } catch (e) {}
    }

    function handleEmailSubmit(ev) {
      ev.preventDefault();
      const email = (emailInput.value || '').trim();
      if (!isValidEmail(email)) {
        emailInput.setAttribute('aria-invalid', 'true');
        if (emailErr) emailErr.hidden = false;
        emailInput.focus();
        return;
      }
      emailInput.removeAttribute('aria-invalid');
      if (emailErr) emailErr.hidden = true;

      const wantsNewsletter = !!(emailNews && emailNews.checked);
      const ranked = rank(state.answers);
      const top = ranked[0];
      const fabricLabel = FABRIC_LABEL[top.fabric] || top.fabric;
      const variant = findVariant(top, config.variants);
      const productTitle = `${top.size}m ${fabricLabel} Classic Bell Tent`;
      const productUrl = (variant && variant.url)
        ? `https://bohobelltent.com${variant.url}`
        : 'https://bohobelltent.com/products/classic-bell-tent';
      const productImage = (variant && variant.image) || config.product_image || '';
      const reasons = reasonsFor(top, state.answers);
      const eventProps = {
        recommended_sku: top.sku,
        recommended_size: top.size + 'm',
        recommended_fabric: top.fabric,
        recommended_fabric_label: fabricLabel,
        recommended_product_title: productTitle,
        recommended_product_url: productUrl,
        recommended_product_image: productImage,
        recommended_price_gbp: top.price,
        recommended_pitch: top.pitch,
        reason_1: reasons[0] || '',
        reason_2: reasons[1] || '',
        reason_3: reasons[2] || '',
        reason_4: reasons[3] || '',
        // Pre-formatted single string for the email body (avoids Klaviyo
        // template-syntax issues when conditionals get pasted into the editor).
        reasons_text: reasons.filter(Boolean).map(r => '• ' + r).join('\n'),
        // Trust strip values (live from Loox shop metafield via Liquid).
        review_rating: (config.trust && config.trust.rating) || '4.83',
        review_count: (config.trust && config.trust.review_count) || '227',
        shipping_threshold: (config.trust && config.trust.shipping_threshold) || '180',
        newsletter_opt_in: wantsNewsletter,
        ...state.answers
      };
      submitToKlaviyo(email, wantsNewsletter, eventProps);
      track('tent_selector_email_submit', { newsletter: wantsNewsletter });
      finish();
    }

    function handleEmailSkip() {
      track('tent_selector_email_skip');
      finish();
    }

    /* Wire up */
    startBtn.addEventListener('click', () => {
      state.step = 0;
      renderQuestion();
      show('question');
      track('tent_selector_start');
    });
    skipBtn.addEventListener('click', skip);
    if (emailForm) emailForm.addEventListener('submit', handleEmailSubmit);
    if (emailSkip) emailSkip.addEventListener('click', handleEmailSkip);

    // Init: show intro
    updateCounter();
  }

  /* -------- Helpers -------- */
  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }
  function escapeAttr(s) { return escapeHtml(s); }

  /* -------- Boot -------- */
  function boot() {
    document.querySelectorAll('[data-tent-selector]').forEach(init);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
